import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { MAX_FILE_NUMBER, TEMP_FILE_SIZE } from '../../../constants';
import {
  fileNotJsonOrCsv,
  fileTooBig,
  formatBytes,
  sentenceCase,
} from '../../../utils/utils';
import FileUpload from '../file-upload/FileUpload';

const StyledTabPanel = styled(TabPanel)({
  padding: 0,
  marginLeft: 8,
  marginRight: -8,
});

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: theme.spacing(0),
}));

const CreateDictionary = ({ handleCreateDictionary }) => {
  const { t } = useTranslation();

  const [openCreateModal, setOpenCreateModal] = useState(false);

  const [tab, setTab] = useState('0');
  const [files, setFiles] = useState([]);
  const [invalidAttributes, setInvalidAttributes] = useState({
    size: false,
    format: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTabChange = (_, newValue) => setTab(newValue);

  const openModal = () => setOpenCreateModal(true);
  const closeModal = () => {
    setOpenCreateModal(false);
    setFiles([]);
    setInvalidAttributes({
      size: false,
      format: false,
    });
    setTab('0');
  };

  const schema = object({
    name: string().required(
      sentenceCase(
        t('inputRequired', {
          ns: 'validations',
          field: t('synonym.dictionaryName', { ns: 'fields' }),
        })
      )
    ),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    shouldUnregister: true,
  });

  const onCreateDictionary = async (data) => {
    setIsSubmitting(true);
    await handleCreateDictionary({ ...data, file: files[0] });
    setIsSubmitting(false);
    closeModal();
  };

  const invalidForm = () => {
    if (tab === '0') return !isValid;
    if (tab === '1') {
      return (
        !isValid ||
        isEmpty(files) ||
        Object.values(invalidAttributes).some((v) => v)
      );
    }
  };

  const removeFile = (fileIndex) => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(fileIndex), 1);
    setFiles(newFiles);
  };

  const onDrop = (acceptedFiles) => {
    if (isEmpty(acceptedFiles) || files.length >= MAX_FILE_NUMBER) return;

    const invalidSize = fileTooBig(acceptedFiles[0].size, TEMP_FILE_SIZE.BYTES);
    const invalidFormat = fileNotJsonOrCsv(acceptedFiles[0].type);

    if (!invalidSize && !invalidFormat) {
      setFiles([...files, ...acceptedFiles]);
    }

    setInvalidAttributes({
      size: invalidSize,
      format: invalidFormat,
    });
  };

  const { getRootProps, open, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <>
      <Button
        id="new-dictionary"
        onClick={openModal}
        aria-label="new dictionary"
      >
        {t('add', {
          ns: 'buttons',
          text: t('synonym.dictionary', { ns: 'project' }),
        })}
      </Button>
      <Dialog open={openCreateModal} fullWidth>
        <TabContext value={tab}>
          <TabList
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label={t('createNew', { ns: 'instance' })} value="0" />
            <Tab label={t('createByImport', { ns: 'instance' })} value="1" />
          </TabList>
          <StyledDialogTitle>
            {t('addition', {
              ns: 'project',
              text: t('synonym.dictionary', { ns: 'project' }),
            })}
          </StyledDialogTitle>
          <form onSubmit={handleSubmit(onCreateDictionary)}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid style={{ padding: 0 }} size={12}>
                  <StyledTabPanel value="0" />
                  <StyledTabPanel value="1">
                    <div {...getRootProps({ className: 'dropzone' })}>
                      <FileUpload
                        style={{ padding: 0 }}
                        files={files}
                        open={open}
                        handleRemove={removeFile}
                        formatError={invalidAttributes.format}
                        sizeError={invalidAttributes.size}
                        sizeLimit={formatBytes(TEMP_FILE_SIZE.BYTES, 0)}
                        helperText={t('dropOneJsonOrCsvFile', {
                          ns: 'instance',
                        })}
                        fileErrorMsg={t('onlyJsonCsvFile', {
                          ns: 'descriptions',
                        })}
                      />
                      <input {...getInputProps()} aria-label="upload file" />
                    </div>
                  </StyledTabPanel>
                </Grid>
                <Grid size={12}>
                  <TextField
                    {...register('name')}
                    id="name"
                    fullWidth
                    autoFocus
                    autoComplete="off"
                    error={!!errors?.name}
                    helperText={errors?.name?.message}
                    label={t('synonym.dictionaryName', { ns: 'fields' })}
                    inputProps={{ 'aria-label': 'dictionary name' }}
                    defaultValue={''}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Box sx={{ mx: 2, mb: 1 }}>
                <Button variant="text" type="reset" onClick={closeModal}>
                  {t('cancel', { ns: 'buttons' })}
                </Button>
                {isSubmitting ? (
                  <Button variant="text" disabled>
                    {t('create', { ns: 'buttons' })}
                    <CircularProgress size={15} style={{ marginLeft: 10 }} />
                  </Button>
                ) : (
                  <Button variant="text" disabled={invalidForm()} type="submit">
                    {t('create', { ns: 'buttons' })}
                  </Button>
                )}
              </Box>
            </DialogActions>
          </form>
        </TabContext>
      </Dialog>
    </>
  );
};

CreateDictionary.propTypes = {
  handleCreateDictionary: PropTypes.func,
};

export default CreateDictionary;
